/**
 * The illustrated layer the mini-games are drawn on.
 *
 * Every game is a small board on a phone held at a dinner table, so the art has two
 * jobs and only two: say *where* you are (the era) and *what you are* (the post).
 * Anything else is noise at that size. Each era gets a backdrop that moves on its own
 * so a paused board still looks alive, and each post gets a sprite instead of the
 * plain circle or square it used to be.
 *
 * Everything here is inline SVG driven by CSS keyframes — no images to load over the
 * bar's wifi, no canvas, and twelve phones can run it at once without heating up.
 * Particle positions are fixed constants rather than Math.random, so a re-render never
 * reshuffles the sky under the player.
 */
import type { CSSProperties, ReactNode } from 'react';

export type Era = 'stars' | 'neon' | 'debris' | 'dust' | 'canopy';

export const ERAS: readonly Era[] = ['stars', 'neon', 'debris', 'dust', 'canopy'];

/** Chapter 1 is Earth 2026; 5 is the green ending. Anything else falls back to stars. */
export function eraOf(chapter: number): Era {
  return ERAS[Math.min(Math.max(chapter, 1), 5) - 1] ?? 'stars';
}

export interface EraSkin {
  /** The era's line colour — sprites and strokes. */
  ink: string;
  /** A translucent version of `ink`, for glows. */
  glow: string;
  /** The board's own background. */
  bg: string;
  /** A second colour, for the parts that should not all read as one. */
  haze: string;
}

export const ERA_SKIN: Record<Era, EraSkin> = {
  stars: { ink: '#6fb7ff', glow: 'rgba(111,183,255,.5)', bg: '#060b18', haze: '#9ad8ff' },
  neon: { ink: '#ff4fd8', glow: 'rgba(255,79,216,.55)', bg: '#0d0618', haze: '#4fe0ff' },
  debris: { ink: '#ff2e3f', glow: 'rgba(255,46,63,.5)', bg: '#120305', haze: '#ff8a3c' },
  dust: { ink: '#e08a3c', glow: 'rgba(224,138,60,.5)', bg: '#140d05', haze: '#ffd8a0' },
  canopy: { ink: '#7fc86a', glow: 'rgba(127,200,106,.5)', bg: '#0e1a0d', haze: '#d6f5a8' },
};

/** Fixed scatter — same sky every render, different-looking sky per era. */
const MOTES: readonly { x: number; y: number; r: number; d: number }[] = [
  { x: 8, y: 14, r: 1.4, d: 0 },
  { x: 23, y: 62, r: 1, d: 1.7 },
  { x: 34, y: 28, r: 1.8, d: 3.1 },
  { x: 47, y: 78, r: 1.1, d: 0.8 },
  { x: 58, y: 41, r: 1.5, d: 2.4 },
  { x: 69, y: 12, r: 1, d: 4.2 },
  { x: 77, y: 66, r: 1.7, d: 1.2 },
  { x: 88, y: 33, r: 1.2, d: 3.6 },
  { x: 94, y: 84, r: 1.4, d: 2 },
  { x: 15, y: 92, r: 1, d: 5 },
];

const fill = (): CSSProperties => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
});

/**
 * The moving backdrop for one era. Sits behind the play surface at low opacity, so it
 * never competes with the thing the guest is actually meant to be tracking.
 */
export function EraBackdrop({ era, opacity = 0.5 }: { era: Era; opacity?: number }) {
  const skin = ERA_SKIN[era];
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ ...fill(), opacity }}
      aria-hidden="true"
    >
      {era === 'stars' && (
        <>
          {/* Earth's limb, bottom left — the last look back. */}
          <circle
            cx="12"
            cy="118"
            r="42"
            fill="none"
            stroke={skin.ink}
            strokeWidth="0.6"
            opacity=".55"
          />
          <circle
            cx="12"
            cy="118"
            r="47"
            fill="none"
            stroke={skin.haze}
            strokeWidth="0.3"
            opacity=".3"
          />
          {MOTES.map((m, i) => (
            <circle
              key={i}
              cx={m.x}
              cy={m.y}
              r={m.r * 0.5}
              fill={skin.haze}
              style={{ animation: `ccTwinkle ${3 + (i % 3)}s ${m.d}s infinite` }}
            />
          ))}
        </>
      )}

      {era === 'neon' && (
        <>
          {/* A skyline you are flying between, not over. */}
          <g opacity=".5">
            {[6, 19, 31, 44, 57, 70, 83, 95].map((x, i) => (
              <rect
                key={x}
                x={x}
                y={38 + ((i * 13) % 34)}
                width="9"
                height="70"
                fill="none"
                stroke={i % 2 ? skin.haze : skin.ink}
                strokeWidth="0.5"
              />
            ))}
          </g>
          {MOTES.slice(0, 7).map((m, i) => (
            <rect
              key={i}
              x={m.x}
              y={m.y}
              width="1.6"
              height="1.6"
              fill={skin.haze}
              style={{ animation: `ccFlicker ${1.4 + (i % 4) * 0.5}s ${m.d}s infinite` }}
            />
          ))}
        </>
      )}

      {era === 'debris' && (
        <>
          {MOTES.map((m, i) => (
            <g
              key={i}
              style={{ animation: `ccFallSlow ${5 + (i % 4) * 1.5}s ${m.d}s linear infinite` }}
            >
              <path
                d={`M${m.x} ${m.y} l${m.r * 2} ${m.r} l-${m.r} ${m.r * 2} l-${m.r * 1.6} -${m.r} z`}
                fill={i % 3 ? skin.ink : skin.haze}
                opacity=".7"
              />
            </g>
          ))}
        </>
      )}

      {era === 'dust' && (
        <>
          {/* Dunes, layered back to front. */}
          <path d="M0 78 Q 25 66 50 76 T 100 72 L100 100 L0 100 z" fill={skin.ink} opacity=".16" />
          <path d="M0 88 Q 30 78 55 87 T 100 84 L100 100 L0 100 z" fill={skin.ink} opacity=".22" />
          {MOTES.map((m, i) => (
            <circle
              key={i}
              cx={m.x}
              cy={m.y}
              r={m.r * 0.6}
              fill={skin.haze}
              opacity=".6"
              style={{ animation: `ccDrift ${6 + (i % 3) * 2}s ${m.d}s linear infinite` }}
            />
          ))}
        </>
      )}

      {era === 'canopy' && (
        <>
          {/* Light coming down through leaves. */}
          {[14, 38, 62, 86].map((x, i) => (
            <path
              key={x}
              d={`M${x} 0 l6 0 l-10 100 l-6 0 z`}
              fill={skin.haze}
              opacity=".08"
              style={{ animation: `ccGlow ${4 + i}s ${i * 0.7}s infinite` }}
            />
          ))}
          {MOTES.slice(0, 8).map((m, i) => (
            <ellipse
              key={i}
              cx={m.x}
              cy={m.y}
              rx={m.r * 1.6}
              ry={m.r * 0.8}
              fill={skin.ink}
              opacity=".45"
              style={{
                transformOrigin: `${m.x}px ${m.y}px`,
                animation: `ccSway ${3 + (i % 3)}s ${m.d}s ease-in-out infinite`,
              }}
            />
          ))}
        </>
      )}
    </svg>
  );
}

/** A board with its era painted on. Every game draws its play surface inside this. */
export function Board({
  era,
  children,
  style,
  flash,
}: {
  era: Era;
  children: ReactNode;
  style?: CSSProperties;
  /** 'hit' tints the board, 'miss' bloods the border — the same language in every game. */
  flash?: 'hit' | 'miss' | null;
}) {
  const skin = ERA_SKIN[era];
  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 14,
        border: `1px solid ${flash === 'miss' ? '#ff2e3f' : `${skin.ink}55`}`,
        background: flash === 'hit' ? `${skin.ink}18` : skin.bg,
        transition: 'background .1s, border-color .1s',
        ...style,
      }}
    >
      <EraBackdrop era={era} />
      {children}
    </div>
  );
}

// -- the posts ---------------------------------------------------------------
// One sprite per profession. Drawn in a 24×24 box so they drop into any board.

/** NAVIGATOR — the ship you are threading. Banks with its climb, thruster flickers. */
export function ShipSprite({
  era,
  tilt = 0,
  dead = false,
}: {
  era: Era;
  tilt?: number;
  dead?: boolean;
}) {
  const skin = ERA_SKIN[era];
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <g
        style={{
          transform: `rotate(${tilt}deg)`,
          transformOrigin: '12px 12px',
          transition: 'transform .12s',
        }}
      >
        <path
          d="M2 12 L17 6 L22 12 L17 18 Z"
          fill={dead ? '#ff2e3f' : skin.bg}
          stroke={dead ? '#ff2e3f' : skin.ink}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M10 12 L17 9.5 L17 14.5 Z" fill={skin.ink} opacity=".55" />
        {/* thruster */}
        <path
          d="M2 12 L-3 10 L-1 12 L-3 14 Z"
          fill={skin.haze}
          style={{ animation: 'ccThrust .18s infinite', transformOrigin: '2px 12px' }}
        />
      </g>
    </svg>
  );
}

/** ENGINEER — a drive pulse running down a coil. */
export function PulseSprite({ era, silent = false }: { era: Era; silent?: boolean }) {
  const skin = ERA_SKIN[era];
  if (silent) {
    return (
      <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
        <circle
          cx="12"
          cy="12"
          r="9"
          fill="none"
          stroke={skin.ink}
          strokeWidth="1.2"
          strokeDasharray="3 3"
          opacity=".5"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill={skin.ink} opacity=".18" />
      <circle cx="12" cy="12" r="7.5" fill="none" stroke={skin.ink} strokeWidth="2" />
      <circle
        cx="12"
        cy="12"
        r="3.4"
        fill={skin.haze}
        style={{ animation: 'ccCore 1s infinite' }}
      />
      <path d="M12 4.5 L12 1 M12 23 L12 19.5" stroke={skin.ink} strokeWidth="1.4" opacity=".8" />
    </svg>
  );
}

/** BIOTECHNICIAN — the growing tip of the vine. */
export function SproutSprite({ era }: { era: Era }) {
  const skin = ERA_SKIN[era];
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <circle cx="12" cy="12" r="8" fill={skin.ink} opacity=".9" />
      <path d="M12 12 C 12 6, 17 4, 19 3 C 19 8, 15 11, 12 12 Z" fill={skin.haze} opacity=".95" />
      <circle cx="9.5" cy="13" r="1.3" fill={skin.bg} />
      <circle cx="14" cy="13" r="1.3" fill={skin.bg} />
    </svg>
  );
}

/** BIOTECHNICIAN — what the vine is reaching for. */
export function SeedSprite({ era }: { era: Era }) {
  const skin = ERA_SKIN[era];
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <g style={{ transformOrigin: '12px 12px', animation: 'ccBob 1.8s ease-in-out infinite' }}>
        <ellipse cx="12" cy="14" rx="5.5" ry="6.5" fill={skin.haze} />
        <path d="M12 8 C 13 4, 16 3, 17 2" stroke={skin.ink} strokeWidth="1.4" fill="none" />
        <ellipse cx="9.8" cy="12.5" rx="1.6" ry="2.4" fill={skin.bg} opacity=".35" />
      </g>
    </svg>
  );
}

/**
 * SIGNAL OFFICER — a dish pad, lit when it speaks.
 *
 * `tint` overrides the era colour, because in a call-and-response game the pad's own
 * colour is the thing being remembered: four identical amber dishes would take the
 * memory cue away and leave only position.
 */
export function DishSprite({ era, lit = false, tint }: { era: Era; lit?: boolean; tint?: string }) {
  const base = ERA_SKIN[era];
  const skin = tint ? { ...base, ink: tint, haze: tint } : base;
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <path
        d="M4 18 A 9 9 0 0 1 20 18 Z"
        fill={lit ? skin.ink : 'none'}
        stroke={skin.ink}
        strokeWidth="1.6"
        opacity={lit ? 1 : 0.75}
      />
      <path d="M12 18 L12 21" stroke={skin.ink} strokeWidth="1.6" />
      {lit && (
        <>
          <path
            d="M12 9 A 5 5 0 0 1 17 13"
            fill="none"
            stroke={skin.haze}
            strokeWidth="1.2"
            style={{ animation: 'ccWave .7s infinite' }}
          />
          <path
            d="M12 5 A 9 9 0 0 1 21 13"
            fill="none"
            stroke={skin.haze}
            strokeWidth="1"
            opacity=".6"
            style={{ animation: 'ccWave .7s .15s infinite' }}
          />
        </>
      )}
    </svg>
  );
}

/** ARCHIVIST — the back of an artifact card, before you turn it over. */
export function RelicBack({ era }: { era: Era }) {
  const skin = ERA_SKIN[era];
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <rect
        x="3"
        y="2"
        width="18"
        height="20"
        rx="2"
        fill="none"
        stroke={skin.ink}
        strokeWidth="1.2"
        opacity=".65"
      />
      <path
        d="M7 7 L17 7 M7 12 L17 12 M7 17 L13 17"
        stroke={skin.ink}
        strokeWidth="1"
        opacity=".35"
      />
      <circle
        cx="12"
        cy="12"
        r="3.6"
        fill="none"
        stroke={skin.haze}
        strokeWidth="1"
        opacity=".5"
        style={{ animation: 'ccGlow 2.6s infinite' }}
      />
    </svg>
  );
}

/** QUARTERMASTER — the thing being split into fair shares. */
export function CrateSprite({ era }: { era: Era }) {
  const skin = ERA_SKIN[era];
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <rect x="3" y="7" width="18" height="13" rx="1.5" fill={skin.ink} opacity=".22" />
      <rect
        x="3"
        y="7"
        width="18"
        height="13"
        rx="1.5"
        fill="none"
        stroke={skin.ink}
        strokeWidth="1.4"
      />
      <path d="M3 11 L21 11" stroke={skin.ink} strokeWidth="1" opacity=".6" />
      <path d="M9 7 L9 20 M15 7 L15 20" stroke={skin.ink} strokeWidth="0.8" opacity=".4" />
      <path d="M7 7 L12 3 L17 7" fill="none" stroke={skin.haze} strokeWidth="1.3" />
    </svg>
  );
}
